import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ShieldAlert, ClipboardList } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { leadsApi, track } from "../api";
import useSeo from "../hooks/useSeo";

const REFERRAL_TYPES = ["DODD / County Board", "Next Generation MyCare", "Professional Referral", "Private Pay / Family"];
const YNU = ["Yes", "No", "Unknown"];
const SERVICES = ["Homemaker Personal Care (HPC)", "HPC Transportation", "Non-Medical Transportation (NMT)",
  "Community Respite", "Residential Respite", "Shared Living", "Participant Directed HPC",
  "Personal Care", "Homemaker Support", "Companionship", "Other"];

const EMPTY = {
  referralType: "", org: "", name: "", email: "", phone: "", city: "", county: "",
  service: "", requestedStart: "", days: "", times: "", weeklyHours: "",
  transportation: "", wheelchair: "", mobility: "", training: "", note: "",
};

const YnuSelect = ({ label, value, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{YNU.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Refer = () => {
  useSeo({
    title: "Refer a Client | Caregivers Alliance Homecare — Ohio DODD & Home Care Referrals",
    description: "Submit a home care referral to Caregivers Alliance Homecare: DODD / County Board, Next Generation MyCare, professional, private-pay and family referrals across Ohio. Call (614) 526-1548.",
    keywords: "refer a client home care Ohio, DODD referral Columbus, county board referral home care, hospital discharge home care referral Ohio",
    path: "/refer",
  });
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const details = [
      form.city && `Client city: ${form.city}`,
      form.days && `Days needed: ${form.days}`,
      form.times && `Times needed: ${form.times}`,
      form.transportation && `Transportation required: ${form.transportation}`,
      form.wheelchair && `Wheelchair vehicle required: ${form.wheelchair}`,
      form.mobility && `Mobility assistance: ${form.mobility}`,
      form.training && `Specialized training requirement: ${form.training}`,
      form.note && `Notes: ${form.note}`,
    ].filter(Boolean).join(" • ");
    try {
      await leadsApi.create({
        name: form.name, contact: form.phone || form.email, email: form.email, phone: form.phone,
        source: "Website", org: form.org || "\u2014",
        referralType: form.referralType, county: form.county, service: form.service,
        requestedStart: form.requestedStart, weeklyHours: form.weeklyHours,
        note: `Referral (${form.referralType || "General"}) — ${details || "No additional details"}`,
      });
      toast({ title: "Referral received!", description: "Our intake team will follow up quickly to coordinate next steps." });
      track("referral_submitted", form.county);
      setForm(EMPTY);
    } catch (err) {
      toast({ title: "Something went wrong", description: err?.response?.status === 429 ? "Too many submissions — please try again shortly." : "Please call us at (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <ClipboardList className="h-4 w-4" /> Referral intake
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">Refer a Client</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            For County Boards, SSAs, discharge planners, case managers, MyCare care coordinators and families.
            We review every referral and respond quickly to coordinate eligibility, authorization and staffing.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-3xl px-4">
          <form onSubmit={submit} data-testid="refer-form" className="bg-white rounded-2xl border border-slate-100 shadow-lg p-7 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Referral Type</Label>
                <Select value={form.referralType} onValueChange={set("referralType")}>
                  <SelectTrigger data-testid="refer-type-select" className="mt-1"><SelectValue placeholder="Select referral type" /></SelectTrigger>
                  <SelectContent>{REFERRAL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Referring Organization</Label>
                <Input data-testid="refer-org-input" value={form.org} onChange={(e) => set("org")(e.target.value)} className="mt-1" placeholder="County Board, hospital, agency…" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input data-testid="refer-name-input" required value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Professional Email</Label>
                <Input data-testid="refer-email-input" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Professional Phone</Label>
                <Input data-testid="refer-phone-input" required value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Client City</Label>
                <Input value={form.city} onChange={(e) => set("city")(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Client County</Label>
                <Input data-testid="refer-county-input" value={form.county} onChange={(e) => set("county")(e.target.value)} className="mt-1" placeholder="e.g. Franklin" />
              </div>
              <div>
                <Label>Requested Service</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="refer-service-select" className="mt-1"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-4">
              <div>
                <Label>Desired Start Date</Label>
                <Input data-testid="refer-start-input" type="date" value={form.requestedStart} onChange={(e) => set("requestedStart")(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Days Needed</Label>
                <Input value={form.days} onChange={(e) => set("days")(e.target.value)} className="mt-1" placeholder="Mon–Fri" />
              </div>
              <div>
                <Label>Times Needed</Label>
                <Input value={form.times} onChange={(e) => set("times")(e.target.value)} className="mt-1" placeholder="Mornings" />
              </div>
              <div>
                <Label>Approx. Weekly Hours</Label>
                <Input data-testid="refer-hours-input" value={form.weeklyHours} onChange={(e) => set("weeklyHours")(e.target.value)} className="mt-1" placeholder="20" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <YnuSelect label="Transportation Required?" value={form.transportation} onChange={set("transportation")} testid="refer-transport-select" />
              <YnuSelect label="Wheelchair Vehicle?" value={form.wheelchair} onChange={set("wheelchair")} testid="refer-wheelchair-select" />
              <YnuSelect label="Mobility Assistance?" value={form.mobility} onChange={set("mobility")} testid="refer-mobility-select" />
              <YnuSelect label="Specialized Training?" value={form.training} onChange={set("training")} testid="refer-training-select" />
            </div>
            <div>
              <Label>Short Notes</Label>
              <Textarea data-testid="refer-notes-input" value={form.note} onChange={(e) => set("note")(e.target.value)} className="mt-1" rows={3} placeholder="Scheduling preferences, referral context…" />
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
              Please do not submit highly sensitive medical information through this general referral form.
              Secure intake documentation will be coordinated when appropriate.
            </div>
            <Button data-testid="refer-submit-btn" type="submit" disabled={busy} className="w-full bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full h-12 font-semibold">
              {busy ? "Submitting…" : "SUBMIT REFERRAL"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Refer;
